import { Loader, Text } from "@mantine/core"
import { notifications } from "@mantine/notifications"
import { Link, RichTextEditor, type RichTextEditorProps } from "@mantine/tiptap"
import Placeholder from "@tiptap/extension-placeholder"
import { Markdown } from "@tiptap/markdown"
import { type EditorOptions, useEditor } from "@tiptap/react"
import StarterKit from "@tiptap/starter-kit"
import { type PropsWithChildren, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useAPIResource } from "@/hooks"
import { slackMessagesAPI } from "@/utils"

import classes from "./MarkdownEditor.module.css"

interface IProps extends PropsWithChildren, Partial<EditorOptions> {
  disabled?: boolean
  label?: string
  onChange: (value: string) => void
  placeholder?: string
  test?: boolean
  toolbar?: boolean
  value?: string
}

export function MarkdownEditor({
  children,
  disabled,
  label,
  onChange,
  placeholder,
  test,
  toolbar = true,
  value,
  ...props
}: IProps & Omit<RichTextEditorProps, "editor" | "children">) {
  const { t } = useTranslation()
  const slack = useAPIResource(slackMessagesAPI)
  const extensions: any[] = [
    StarterKit,
    Markdown,
    Link.extend({}).configure({
      protocols: ["tel", "mailto", "slack"],
    }),
  ]
  if (placeholder) extensions.push(Placeholder.configure({ placeholder }))

  const editor = useEditor({
    extensions,
    immediatelyRender: false,
    onUpdate: ({ editor }) => {
      const value = editor.getMarkdown()
      if (onChange) onChange(value)
    },
  })

  useEffect(() => {
    editor?.setEditable(!disabled, false)
  }, [disabled, editor])

  useEffect(() => {
    if (value === undefined) return
    if (editor?.getMarkdown() === value) return

    editor?.commands.setContent(value, {
      emitUpdate: false,
      contentType: "markdown",
    })
  }, [value, editor])

  return (
    <div>
      {label && (
        <Text fw={500} fz="sm" component="label">
          {label}
        </Text>
      )}
      <RichTextEditor
        editor={editor}
        data-disabled={disabled}
        data-toolbar={toolbar}
        variant="subtle"
        classNames={classes}
        {...props}
      >
        {toolbar && (
          <RichTextEditor.Toolbar sticky stickyOffset="var(--docs-header-height)">
            <RichTextEditor.ControlsGroup>
              <RichTextEditor.Bold disabled={disabled} />
              <RichTextEditor.Italic disabled={disabled} />
              <RichTextEditor.Underline disabled={disabled} />
              <RichTextEditor.Strikethrough disabled={disabled} />
              <RichTextEditor.ClearFormatting disabled={disabled} />
              <RichTextEditor.Highlight disabled={disabled} />
              <RichTextEditor.Code disabled={disabled} />
            </RichTextEditor.ControlsGroup>
            <RichTextEditor.ControlsGroup>
              <RichTextEditor.Link disabled={disabled} />
              <RichTextEditor.Unlink disabled={disabled} />
            </RichTextEditor.ControlsGroup>
            <RichTextEditor.ControlsGroup>
              <RichTextEditor.Undo disabled={disabled} />
              <RichTextEditor.Redo disabled={disabled} />
            </RichTextEditor.ControlsGroup>
            {children}
            {test && (
              <RichTextEditor.ControlsGroup ml="auto">
                <RichTextEditor.Control
                  px={10}
                  title="Test Message"
                  fz="xs"
                  disabled={disabled || slack.isUpdating}
                  onClick={async () => {
                    await slack.create({ body: value || "" })
                    notifications.show({
                      message: t("Success"),
                      color: "green",
                    })
                  }}
                >
                  {slack.isUpdating && <Loader size={16} color="gray" type="oval" mr="xs" />}
                  {t("test")}
                </RichTextEditor.Control>
              </RichTextEditor.ControlsGroup>
            )}
          </RichTextEditor.Toolbar>
        )}
        <RichTextEditor.Content />
      </RichTextEditor>
    </div>
  )
}
